<script lang="ts">
  import { onMount, type Snippet } from "svelte"
  import Statement from "./PlanReview/Statement.svelte"
  import Swot from "./PlanReview/Swot.svelte"
  import consumer from "$app/channels/consumer"
  import {
    strengthIcon,
    weaknessIcon,
    opportunityIcon,
    threatIcon,
    wait,
    toast
  } from "$app/utils"
  import { appStore } from "$app/store.svelte"
  import { Plus } from "lucide-svelte"
  import { STATEMENT_SECTIONS } from "$app/constants"
  import Modal from "$app/components/Modal.svelte"
  import StatementEdit from "$app/Pages/Plans/OnePageView/StatementEdit.svelte"
  import emptyImg from "@images/onboarding/empty.png"

  interface Props {
    editable: boolean
    actionCable: boolean
    header?: Snippet
  }

  let { editable, actionCable, header }: Props = $props()
  const current_user = appStore.current_user

  export function valid(): boolean {
    return true
  }

  let openValuesModal = $state(false)
  let editStatus = $state("")
  if (appStore.meeting) {
    appStore.channel_id = `meeting_plan_${appStore.meeting.uniq_id}`
  }

  /**********/
  function setActionCableChannel(): void {
    consumer.subscriptions.create(
      {
        channel: "PlanChannel",
        room: appStore.meeting.uniq_id
      },
      {
        // connected() {},
        // disconnected() {},
        async received(data: any) {
          let title: string
          if (data.statements_sorted) {
            const sel = data.statements_sorted[0]
            appStore.updateStatements(data.statements_sorted, sel.section_type)

            if (data.user_id !== current_user.id) {
              toast(`${getTitle(sel)} order has been updated`, "success")
            }
          }

          if (data.statement_updated) {
            title =
              STATEMENT_SECTIONS[data.statement_updated?.section_type]?.singular

            appStore.updateStatement(data.statement_updated)

            const msg = `${getTitle(
              data.statement_updated
            )} ${hasSingularOrPlural(
              data.statement_updated.section_type
            )} been updated`

            if (data.user_id !== current_user.id) {
              toast(msg, "success")
            }
          }

          if (data.statement_created && data.user_id !== current_user.id) {
            const secType = data?.statement_created?.section_type
            // Required beacuse we use components from Plan that can't sync
            const prevStatements = [...appStore.statements]

            await wait(50)
            appStore.statements = []
            appStore.statements = [
              ...prevStatements,
              { ...data.statement_created, selected: false }
            ]

            const title = STATEMENT_SECTIONS[secType]?.name
            toast(`${title} has been created`, "success")
          }

          if (data.statement_deleted && data.user_id !== current_user.id) {
            const secType = data?.statement_deleted?.section_type
            const title = STATEMENT_SECTIONS[secType]?.name
            const msg = `${title} has been deleted`

            const idx = appStore.statements.findIndex(
              (st) => data.statement_deleted.id === st.id
            )
            appStore.statements.splice(idx, 1)

            await wait(50)
            appStore.statements = [...appStore.statements]

            toast(msg, "success")
          }
        }
      }
    )
  }

  function hasSingularOrPlural(section: string): string {
    return ["BrandPurpose", "BlueSkyVision"].includes(section) ? "has" : "have"
  }

  function getTitle(statement: StatementType): string {
    if (["BrandPurpose", "BlueSkyVision"].includes(statement.section_type)) {
      return STATEMENT_SECTIONS[statement?.section_type]?.name
    } else {
      return STATEMENT_SECTIONS[statement?.section_type]?.plural
    }
  }

  let brand_purpose = $derived(
    appStore.statements
      .filter((st) => st.section_type === "BrandPurpose")
      .sort((a, b) => a.position - b.position)[0] || ({} as StatementType)
  )

  let values = $derived(
    appStore.statements
      .filter((st) => st.section_type === "CoreValue")
      .sort((a, b) => a.position - b.position)
      .slice(0, 5)
  )

  let blue_sky_vision = $derived(
    appStore.statements
      .filter((st) => st.section_type === "BlueSkyVision")
      .sort((a, b) => a.position - b.position)[0] || ({} as StatementType)
  )

  let strengths = $derived(
    appStore.statements
      .filter((st) => st.section_type === "Strength")
      .sort((a, b) => a.position - b.position)
  )

  let weaknesses = $derived(
    appStore.statements
      .filter((st) => st.section_type === "Weakness")
      .sort((a, b) => a.position - b.position)
  )

  let opportunities = $derived(
    appStore.statements
      .filter((st) => st.section_type === "Opportunity")
      .sort((a, b) => a.position - b.position)
  )

  let threats = $derived(
    appStore.statements
      .filter((st) => st.section_type === "Threat")
      .sort((a, b) => a.position - b.position)
  )

  onMount(() => {
    if (actionCable) {
      setActionCableChannel()
    }
  })
</script>

{#if header}
  {@render header()}
{/if}

<div class="w-full grid grid-cols-12 gap-4 mt-6">
  <div class="md:col-span-3 shadow" id="brand-purpose">
    <Statement
      statement={brand_purpose}
      {editable}
      {actionCable}
      title="Brand Purpose"
      section="BrandPurpose"
    />
  </div>

  <div class="col-span-12 md:col-span-9 bg-white shadow relative" id="values">
    <div class="col-span-4 px-4 py-5 sm:px-6">
      <h2 class="leading-6 font-bold text-black text-center">Values</h2>
    </div>
    <!--Start of Values-->
    <div class="flex justify-around" data-name="values">
      {#each values as val (val.id)}
        <Statement
          statement={val}
          editable={false}
          height="h-56 value"
          section="CoreValue"
          {actionCable}
        />
      {:else}
        <div class="flex justify-center col-span-5">
          <div class="flex flex-col justify-center items-center">
            <img src={emptyImg} class="w-16 h-auto" alt="No Brand Purpose" />
            <button
              class="mt-6 border py-2 px-4 rounded flex hover:bg-gray-100 text-gray-800 items-center justify-center"
              type="button"
              onclick={() => {
                editStatus = "new"
                openValuesModal = true
              }}
            >
              <Plus class="text-gray-600" />
              <div class="text-center text-sm ml-1">Create your Values</div>
            </button>
          </div>
        </div>
      {/each}

      {#if editable}
        <div class="w-12 absolute top-2 right-2">
          <button
            type="button"
            class="btn-link"
            data-name="edit-values"
            onclick={() => {
              editStatus = ""
              appStore.trackStatement.status = "edit"
              openValuesModal = true
            }}
          >
            EDIT
          </button>
        </div>
      {/if}
    </div>
    <!--End of Values-->
  </div>
</div>

<div class="w-full grid grid-cols-12 gap-4 mt-4">
  <div class="col-span-12 md:col-span-3" id="blue-sky-vision">
    <Statement
      statement={blue_sky_vision}
      {editable}
      {actionCable}
      title="Blue Sky Vision"
      section="BlueSkyVision"
    />
  </div>
  <div class="col-span-12 md:col-span-9">
    <div class="w-full grid grid-cols-2 md:grid-cols-4 gap-4">
      <div id="swot-strengths">
        <Swot
          statements={strengths}
          title="Strengths"
          sectionType="Strength"
          icon={strengthIcon}
          {editable}
          {actionCable}
        />
      </div>
      <div id="swot-weaknesses">
        <Swot
          statements={weaknesses}
          title="Weaknesses"
          sectionType="Weakness"
          icon={weaknessIcon}
          {editable}
          {actionCable}
        />
      </div>
      <div id="swot-opportunities">
        <Swot
          statements={opportunities}
          title="Opportunities"
          sectionType="Opportunity"
          icon={opportunityIcon}
          {editable}
          {actionCable}
        />
      </div>
      <div id="swot-threats">
        <Swot
          statements={threats}
          title="Threats"
          sectionType="Threat"
          icon={threatIcon}
          {editable}
          {actionCable}
        />
      </div>
    </div>
  </div>
</div>

<Modal bind:open={openValuesModal} large={true}>
  <StatementEdit
    section="CoreValue"
    statements={appStore.statements.filter(
      (st) => st.section_type === "CoreValue"
    )}
    onupdate={(st) => {
      if (!actionCable) {
        appStore.updateStatement(st)
      }
    }}
    initialStatus={editStatus}
    symbolPicker={true}
  />
</Modal>
